
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Navbar from '../../components/Navbar';
import { getOrganizationSchema, getWebPageSchema } from '../../utils/structuredData';

export default function Home() {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      getOrganizationSchema(),
      getWebPageSchema(
        'Minchio Brothers - Professional Real Estate Photography & Videography',
        'Award-winning videography and photography services for Real Estate, Commercial, and Social Media in Australia',
        'https://minchiobrothers.au/'
      )
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Minchio Brothers - Award-Winning Real Estate Photography & Videography Australia"
        description="Professional real estate photography and videography services in Australia. High-quality property photos, drone photography, virtual tours, and commercial content. 500+ projects completed with 24hr response time."
        keywords="real estate photography Australia, property photography, commercial videography, drone photography, virtual tours, social media content, real estate marketing"
        canonicalUrl="/"
        structuredData={structuredData}
      />
      {/* Navigation */}
      <Navbar variant="dark" position="fixed" />

      {/* Hero Section with Auto-Playing Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background - Desktop */}
        <div className="absolute inset-0 w-full h-full hidden md:block">
          <iframe
            src="https://www.youtube.com/embed/YIPkAbm9RVQ?autoplay=1&mute=1&loop=1&playlist=YIPkAbm9RVQ&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1&vq=hd1440"
            title="Minchio Brothers Showreel Background"
            className="w-full h-full object-cover"
            style={{ minWidth: '100%', minHeight: '100%', transform: 'scale(1.1)' }}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
          {/* Video Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
        </div>

        {/* Video Background - Mobile (Shorts) */}
        <div className="absolute inset-0 w-full h-full md:hidden">
          <iframe
            src="https://www.youtube.com/embed/6fjQ9yKreGA?autoplay=1&mute=1&loop=1&playlist=6fjQ9yKreGA&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1"
            title="Minchio Brothers Mobile Showreel"
            className="w-full h-full object-cover"
            style={{ minWidth: '100%', minHeight: '100%', transform: 'scale(1.1)' }}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
          {/* Video Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <div className="mb-6">
            <span className="inline-block px-6 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-semibold text-sm tracking-wider border border-white/30">
              AWARD-WINNING VIDEOGRAPHY
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-4 tracking-wider drop-shadow-2xl">
            MINCHIO BROTHERS
          </h1>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-wider drop-shadow-2xl">
            PRODUCTIONS
          </h2>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8 font-light drop-shadow-lg">
            Creating cinematic stories that captivate audiences across Real Estate, Commercial, and Social Media
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link 
              to="/contact" 
              className="inline-block bg-white text-black px-8 py-3 text-lg font-semibold hover:bg-gray-200 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-2xl"
            >
              START YOUR PROJECT
            </Link>
            <Link 
              to="/real-estate" 
              className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 text-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-2xl"
            >
              REAL ESTATE PACKAGES
            </Link>
            <button 
              onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-block bg-transparent border-2 border-white text-white px-8 py-3 text-lg font-semibold hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap shadow-2xl backdrop-blur-sm"
            >
              VIEW OUR WORK
            </button>
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-12">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">500+</div>
              <div className="text-sm text-white/80">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">3</div>
              <div className="text-sm text-white/80">Service Areas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">24hr</div>
              <div className="text-sm text-white/80">Response Time</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-6 py-2 bg-gray-100 rounded-full text-gray-700 font-semibold text-sm tracking-wider border border-gray-200 mb-6">
              OUR EXPERTISE
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Professional Video Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specializing in three core areas that drive results for our clients
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Real Estate */}
            <Link to="/real-estate" className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <img 
                  src="https://customer-assets.emergentagent.com/job_mincham-appointments/artifacts/11ws2pwc_IMG_8304.jpg"
                  alt="Real Estate Videography"
                  className="w-full h-64 object-cover object-center transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">Real Estate</h3>
                    <p className="text-gray-200 mb-4">Transform properties into must-see experiences</p>
                    <div className="flex items-center text-sm">
                      <span className="bg-teal-500 px-3 py-1 rounded-full mr-2">Professional</span>
                      <span>4K Video + Photos</span>
                    </div>
                  </div>
                </div>
                <div className="absolute top-4 right-4">
                  <div className="bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                </div>
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Real Estate Videography</h3>
                <p className="text-gray-600">Professional property showcases that sell homes faster</p>
              </div>
            </Link>

            {/* Commercial */}
            <Link to="/commercial" className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <img 
                  src="https://customer-assets.emergentagent.com/job_mincham-appointments/artifacts/778rc7uh_nqsprings.png"
                  alt="Commercial Videography"
                  className="w-full h-64 object-cover object-center transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">Commercial</h3>
                    <p className="text-gray-200 mb-4">Elevate your brand with cinematic storytelling</p>
                    <div className="flex items-center text-sm">
                      <span className="bg-slate-500 px-3 py-1 rounded-full mr-2">Custom Quote</span>
                      <span>Brand Stories</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Commercial Production</h3>
                <p className="text-gray-600">Brand stories that connect with your audience</p>
              </div>
            </Link>

            {/* Social Media */}
            <Link to="/social-media" className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <img 
                  src="https://customer-assets.emergentagent.com/job_mincham-appointments/artifacts/n97colrt_IMG_8305.jpg"
                  alt="Social Media Content"
                  className="w-full h-64 object-cover object-center transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">Social Media</h3>
                    <p className="text-gray-200 mb-4">Viral-ready content for all platforms</p>
                    <div className="flex items-center text-sm">
                      <span className="bg-purple-500 px-3 py-1 rounded-full mr-2">Custom Quote</span>
                      <span>Reels & Posts</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Social Media Content</h3>
                <p className="text-gray-600">Engaging content that grows your following</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Portfolio Section */}
      <section id="portfolio" className="py-20 bg-black text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-6 py-2 bg-white/10 rounded-full text-white font-semibold text-sm tracking-wider border border-white/20 mb-6">
              FEATURED WORK
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-wider">
              Recent Projects
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Showcasing our latest work across different industries and platforms
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Featured Video 1 */}
            <div className="group cursor-pointer">
              <div className="relative mb-6 overflow-hidden rounded-xl">
                <img 
                  src="https://img.youtube.com/vi/qYwzEzuPzek/maxresdefault.jpg"
                  alt="ACC National Commercial"
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-play-fill text-white text-2xl"></i>
                  </div>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-slate-600 text-white px-3 py-1 rounded-full text-sm font-medium">Commercial</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">ACC National Brand Story</h3>
              <p className="text-gray-400 text-sm">Professional commercial showcasing brand excellence</p>
            </div>

            {/* Featured Video 2 */}
            <div className="group cursor-pointer">
              <div className="relative mb-6 overflow-hidden rounded-xl">
                <img 
                  src="https://img.youtube.com/vi/wtCqoBYhetM/maxresdefault.jpg"
                  alt="Luxury Property Showcase"
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-play-fill text-white text-2xl"></i>
                  </div>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-medium">Real Estate</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Luxury Living On Castle Hill</h3>
              <p className="text-gray-400 text-sm">Premium real estate showcase on Castle Hill</p>
            </div>

            {/* Featured Video 3 */}
            <div className="group cursor-pointer">
              <div className="relative mb-6 overflow-hidden rounded-xl">
                <img 
                  src="https://img.youtube.com/vi/6qTWzvroJ_8/maxresdefault.jpg"
                  alt="Social Media Content"
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-play-fill text-white text-2xl"></i>
                  </div>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">Social Media</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Viral Social Content</h3>
              <p className="text-gray-400 text-sm">Engaging content optimized for social platforms</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/real-estate" 
              className="inline-block bg-white text-black px-8 py-4 text-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap mr-4"
            >
              VIEW ALL WORK
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-6 py-2 bg-gray-200 rounded-full text-gray-700 font-semibold text-sm tracking-wider border border-gray-300 mb-6">
              WHY CHOOSE US
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              The Minchio Brothers Difference
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-camera-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">4K Cinematic Quality</h3>
              <p className="text-gray-600">Professional-grade equipment and techniques for stunning visual results</p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Fast Turnaround</h3>
              <p className="text-gray-600">Same-day delivery available with professional editing and color grading</p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-award-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Award-Winning Team</h3>
              <p className="text-gray-600">Experienced professionals with a proven track record of excellence</p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-map-pin-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Local Expertise</h3>
              <p className="text-gray-600">Serving Townsville, Brisbane, and Gold Coast with local market knowledge</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 tracking-tight">Ready to Create Something Amazing?</h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Let's bring your vision to life with professional videography that captures every moment perfectly and drives real results.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to="/contact" 
              className="inline-block bg-white text-black px-10 py-4 text-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              GET FREE QUOTE
            </Link>
            <a 
              href="tel:+61431323137"
              className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 text-lg font-semibold hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              CALL NOW: +61 431 323 137
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div className="md:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://images.squarespace-cdn.com/content/v1/6329980709c417765c18ef9c/1663928993488-9PHELX1GHR1MGT4SNOLP/minchio+brothers+logo.png?format=100w" 
                  alt="Minchio Brothers Logo" 
                  className="h-10 w-10"
                />
                <span className="text-gray-900 font-bold text-lg">MINCHIO BROTHERS</span>
              </div>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Professional videography services across Queensland, specializing in Real Estate, Commercial, and Social Media content.
              </p>
              <div className="flex space-x-3">
                <a href="https://www.instagram.com/minchiobrothers/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-white text-lg"></i>
                </a>
                <a href="mailto:media@matthewminchio.com" className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer">
                  <i className="ri-mail-fill text-white text-lg"></i>
                </a>
                <a href="tel:+61431323137" className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer">
                  <i className="ri-phone-fill text-white text-lg"></i>
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-gray-900 font-bold mb-4">Services</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/real-estate" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Real Estate Videography</Link></li>
                <li><Link to="/commercial" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Commercial Production</Link></li>
                <li><Link to="/social-media" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Social Media Content</Link></li>
                <li><span className="text-gray-600">Drone Videography</span></li>
                <li><span className="text-gray-600">Event Coverage</span></li>
                <li><span className="text-gray-600">Brand Storytelling</span></li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-gray-900 font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/reviews" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Client Reviews</Link></li>
                <li><Link to="/contact" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Contact Us</Link></li>
                <li><Link to="/contact" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Get Quote</Link></li>
                <li><a href="https://www.minchsolutions.au" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Minch Solutions</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-gray-900 font-bold mb-4">Contact Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <i className="ri-phone-line text-gray-600 mt-0.5"></i>
                  <div>
                    <a href="tel:+61431323137" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer font-medium">+61 431 323 137</a>
                    <p className="text-gray-500 text-xs">Available 9AM - 6PM</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-mail-line text-gray-600 mt-0.5"></i>
                  <div>
                    <a href="mailto:media@matthewminchio.com" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer font-medium">media@matthewminchio.com</a>
                    <p className="text-gray-500 text-xs">24hr response time</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-map-pin-line text-gray-600 mt-0.5"></i>
                  <div>
                    <p className="text-gray-600 font-medium">Service Areas:</p>
                    <p className="text-gray-500 text-xs">Townsville, Brisbane, Gold Coast</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-6 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">© 2021 MINCHIO BROTHERS. ALL RIGHTS RESERVED</p>
            <div className="flex items-center space-x-4 text-sm">
              <a href="https://www.minchsolutions.au" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 cursor-pointer">
                Made by Minchsolutions
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
